import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { EventFailureAPI } from '../../../services/Hub/notificationCenter/eventFailure.api';
import {
  EventFailureDTO,
  EVENT_RETRY_STATUS
} from '../../../services/Hub/notificationCenter/types';

export type PaginationProps = {
  page: number;
  limit: number;
  filter?: {
    status?: EVENT_RETRY_STATUS;
  };
};

export const useFetchPaginatedEventFailures = () => {
  const [eventFailures, setEventFailures] = useState<EventFailureDTO[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<EVENT_RETRY_STATUS | undefined>(undefined);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [totalEntities, setTotalEntities] = useState<number>(1);

  const { enqueueSnackbar } = useSnackbar();

  const updateOneItem = useCallback((update: EventFailureDTO) => {
    setEventFailures(old => old.map(e => (e.id === update.id ? update : e)));
  }, []);

  const fetchPage = useCallback(
    async (props: PaginationProps) => {
      setIsFetching(true);

      try {
        const data = await EventFailureAPI.fetchPaginated({
          status: selectedStatus,
          page: props.page,
          limit: props.limit
        });
        setTotalEntities(data.total);
        setEventFailures(data.results);
      } catch (e) {
        enqueueSnackbar(`Error Fetching Page ${props.page}`, { variant: 'error' });
        throw e;
      } finally {
        setIsFetching(false);
      }
    },
    [enqueueSnackbar, selectedStatus]
  );

  const fetchNextPage = useCallback(
    async (model: { page: number; pageSize: number }, onFailure?: () => void) => {
      const { page, pageSize } = model;

      const newPageProps = {
        page: page + 1,
        limit: pageSize
      };
      try {
        await fetchPage(newPageProps);
      } catch (e) {
        onFailure?.();
      }
    },
    [fetchPage]
  );

  const debouncedFetch = useMemo(
    () =>
      debounce(() => {
        fetchNextPage({ page: 0, pageSize: 20 });
      }, 300),
    [fetchNextPage]
  );

  useEffect(() => {
    debouncedFetch();
  }, [debouncedFetch]);

  const updateEvent = useCallback(
    async (id: string) => {
      try {
        const data = await EventFailureAPI.getOne(id);
        updateOneItem(data);
      } catch (err) {
        enqueueSnackbar('error requesting retry');
      }
    },
    [enqueueSnackbar, updateOneItem]
  );

  const retryEvent = useCallback(
    async (id: string) => {
      try {
        const data = await EventFailureAPI.requestRetry(id);
        updateOneItem(data);
        setTimeout(() => {
          updateEvent(id)
            .then(() => {
              console.debug('updated row');
            })
            .catch(e => {
              console.error('error updating row', e);
            });
        }, 120000);
      } catch (err) {
        enqueueSnackbar('error requesting retry');
      }
    },
    [enqueueSnackbar, updateEvent, updateOneItem]
  );

  const ignoreEvent = useCallback(
    async (id: string) => {
      try {
        const data = await EventFailureAPI.ignoreEvent(id);
        updateOneItem(data);
      } catch (err) {
        enqueueSnackbar('error ignoring event');
      }
    },
    [enqueueSnackbar, updateOneItem]
  );

  return {
    totalEntities,
    eventFailures,
    fetchNextPage,
    isFetching,
    updateOneItem,
    selectedStatus,
    setSelectedStatus,
    retryEvent,
    updateEvent,
    ignoreEvent
  };
};
